import { CheckIcon, Square2StackIcon } from "@heroicons/react/16/solid"
import { cn } from "@src/utils/cn"
import clsx from "clsx"
import {
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react"

interface CopyButtonProps {
  text: string | (() => string)
  ariaLabel?: string
  className?: string
}

export function CopyButton({ text, ariaLabel, className }: CopyButtonProps) {
  return (
    <Copy text={text}>
      {(copied) => (
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            "relative flex size-5 items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-white",
            className
          )}
        >
          <CheckIcon
            className={clsx(
              "absolute size-4 transition duration-200",
              copied
                ? "scale-100 opacity-100 blur-none"
                : "scale-0 opacity-0 blur-xs"
            )}
          />
          <Square2StackIcon
            className={clsx(
              "absolute size-4 transition duration-200",
              copied && "scale-0 opacity-0 blur-xs"
            )}
          />
        </button>
      )}
    </Copy>
  )
}

export function Copy({
  children,
  text,
}: {
  children: (copied: boolean) => ReactNode
  text: string | (() => string)
}) {
  const [copied, setCopied] = useState(false)
  const abortCtrlRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      abortCtrlRef.current?.abort()
    }
  }, [])

  const handleClick = async () => {
    abortCtrlRef.current?.abort()
    abortCtrlRef.current = new AbortController()

    try {
      const t = typeof text === "function" ? text() : text
      await navigator.clipboard.writeText(t)
    } catch {
      return
    }

    if (abortCtrlRef.current?.signal.aborted) {
      return
    }

    let timerId: ReturnType<typeof setTimeout>
    if (!copied) {
      setCopied(true)
      timerId = setTimeout(() => {
        setCopied(false)
      }, 2000)
    } else {
      setCopied(false)

      timerId = setTimeout(() => {
        setCopied(true)
        timerId = setTimeout(() => {
          setCopied(false)
        }, 2000)
      }, 125)
    }

    abortCtrlRef.current.signal.addEventListener("abort", () => {
      clearTimeout(timerId)
    })
  }

  const child = children(copied)

  if (isValidElement(child)) {
    return cloneElement(child as ReactElement<{ onClick?: () => void }>, {
      onClick: handleClick,
    })
  }

  return child
}
